/**
 * Template Detail Page
 *
 * Shows a single environment template with tabs for Instances, Configuration,
 * and Template artifacts. Ported from EnvironmentTemplateDetail with sandbox-area
 * breadcrumbs and routing.
 *
 * @module features/sandboxes/pages/TemplateDetailPage
 */

import React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Page,
  PageHeader,
  Text,
  Flex,
  Button,
  LoadingOrbit,
  useToast,
  UnderlineTabs,
  UnderlineTab,
  TextField,
} from '@chatsdet/ui';
import { INSTANCE_SIZE_SPEC, type InstanceSize } from '@chatsdet/types';
import { Cube, GitBranch, PencilSimple, Check, X } from '@phosphor-icons/react';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import { useAuth } from '../../../contexts/AuthContext';
import {
  useEnvironmentTemplate,
  useDeleteTemplate,
  useUpdateTemplateConfig,
  useUpdateEmulationConfig,
  useUpdateInstanceSize,
  useUpdateTemplateName,
  useStartAuthoring,
  useRegenerateTemplate,
  type EmulationTemplateConfig,
} from '../../../hooks/useEnvironmentTemplates';
import { ConfigurationTab } from '../components/ConfigurationTab';
import { TemplateTab } from '../components/TemplateTab';
import { TemplateActionsTab } from '../components/TemplateActionsTab';
import { buildTemplateConfigPatch } from '../template-config-defaults';
import {
  TemplateInstancesSection,
  type TemplateInstanceStats,
} from '../components/TemplateInstancesSection';
import { TemplateDeleteDialog } from '../components/TemplateDeleteDialog';
import { TemplateStatusPanels } from '../components/TemplateStatusPanels';
import { SandboxStatusBadge } from '../components/SandboxStatusBadge';
import { formatRelative } from '../components/overview/time-format';

type TabId = 'instances' | 'configuration' | 'actions' | 'template';

export const TemplateDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { templateId } = useParams({ from: '/sandboxes/templates/$templateId' });
  const { toast } = useToast();
  const { user } = useAuth();

  // Tab state
  const [activeTab, setActiveTab] = React.useState<TabId>('instances');

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [instanceStats, setInstanceStats] = React.useState<TemplateInstanceStats>({
    total: 0,
    active: 0,
  });

  const hasAccess = !!user?.features?.environments;

  const templateQuery = useEnvironmentTemplate(hasAccess ? templateId : null);
  const deleteMutation = useDeleteTemplate();
  const updateConfigMutation = useUpdateTemplateConfig();
  const updateEmulationMutation = useUpdateEmulationConfig();
  const updateInstanceSizeMutation = useUpdateInstanceSize();
  const updateNameMutation = useUpdateTemplateName();
  const startAuthoringMutation = useStartAuthoring();
  const regenerateMutation = useRegenerateTemplate();

  // Inline header rename state
  const [isEditingName, setIsEditingName] = React.useState(false);
  const [nameDraft, setNameDraft] = React.useState('');

  // Config defaults editing state
  const [configDefaults, setConfigDefaults] = React.useState<Record<string, string>>({});
  const [dirtyConfigKeys, setDirtyConfigKeys] = React.useState<Set<string>>(new Set());
  const [unsetConfigKeys, setUnsetConfigKeys] = React.useState<Set<string>>(new Set());
  const [visibleFields, setVisibleFields] = React.useState<Record<string, boolean>>({});

  // Emulation config editing state
  const [emulationConfig, setEmulationConfig] = React.useState<EmulationTemplateConfig>({
    enabled: false,
    plugins: [],
  });
  const [emulationDirty, setEmulationDirty] = React.useState(false);

  const template = templateQuery.data?.data;

  // Initialize config defaults when template loads
  React.useEffect(() => {
    if (template?.configSchema) {
      const defaults: Record<string, string> = {};
      template.configSchema.forEach((field) => {
        defaults[field.key] = template.configDefaults?.[field.key] ?? '';
      });
      setConfigDefaults(defaults);
      setDirtyConfigKeys(new Set());
      setUnsetConfigKeys(new Set());
    }
  }, [template?.configSchema, template?.configDefaults, template?.configuredSensitiveKeys]);

  // Initialize emulation config when template loads
  React.useEffect(() => {
    if (template) {
      setEmulationConfig(template.emulationConfig ?? { enabled: false, plugins: [] });
      setEmulationDirty(false);
    }
  }, [template?.emulationConfig]);

  // Redirect if user doesn't have access
  React.useEffect(() => {
    if (user && !hasAccess) {
      void navigate({ to: '/' });
    }
  }, [user, hasAccess, navigate]);

  useBreadcrumbs(
    template
      ? [
          { label: 'Test Environments', onPress: () => navigate({ to: '/sandboxes' }) },
          { label: template.name },
        ]
      : [{ label: 'Test Environments', onPress: () => navigate({ to: '/sandboxes' }) }]
  );

  const handleDelete = async () => {
    if (!templateId) return;
    try {
      await deleteMutation.mutateAsync(templateId);
      toast.success('Template deleted');
      setDeleteDialogOpen(false);
      navigate({ to: '/sandboxes' });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete template');
    }
  };

  const handleSaveConfig = async () => {
    if (!templateId) return;
    try {
      await updateConfigMutation.mutateAsync({
        templateId,
        ...buildTemplateConfigPatch(configDefaults, dirtyConfigKeys, unsetConfigKeys),
      });
      toast.success('Configuration defaults saved');
      setDirtyConfigKeys(new Set());
      setUnsetConfigKeys(new Set());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save configuration');
    }
  };

  const handleSaveEmulation = async () => {
    if (!templateId) return;
    try {
      await updateEmulationMutation.mutateAsync({
        templateId,
        emulationConfig,
      });
      toast.success('Emulation configuration saved');
      setEmulationDirty(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save emulation config');
    }
  };

  const handleUpdateEmulation = (config: EmulationTemplateConfig) => {
    setEmulationConfig(config);
    setEmulationDirty(true);
  };

  const updateConfigDefault = (key: string, value: string) => {
    setConfigDefaults((prev) => ({ ...prev, [key]: value }));
    setDirtyConfigKeys((prev) => new Set(prev).add(key));
    setUnsetConfigKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const clearConfigDefault = (key: string) => {
    setConfigDefaults((prev) => ({ ...prev, [key]: '' }));
    setDirtyConfigKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setUnsetConfigKeys((prev) => new Set(prev).add(key));
  };

  const toggleFieldVisibility = (key: string) => {
    setVisibleFields((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleStartAuthoring = async (mode: 'author' | 'explore' = 'author') => {
    if (!templateId) return;
    try {
      const result = await startAuthoringMutation.mutateAsync({ templateId, mode });
      toast.success(mode === 'explore' ? 'Explore session started' : 'Authoring started');
      navigate({
        to: '/sandboxes/$sandboxId',
        params: { sandboxId: result.data.instanceId },
        search: { tab: 'authoring' },
      });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : `Failed to start ${mode === 'explore' ? 'explore session' : 'authoring'}`
      );
    }
  };

  const handleRegenerate = async () => {
    if (!templateId) return;
    try {
      await regenerateMutation.mutateAsync({ templateId });
      toast.success('Regeneration started');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to regenerate template');
    }
  };

  const startEditName = (currentName: string) => {
    setNameDraft(currentName);
    setIsEditingName(true);
  };

  const cancelEditName = () => {
    setIsEditingName(false);
    setNameDraft('');
  };

  const submitName = async (currentName: string) => {
    const next = nameDraft.trim();
    if (!templateId || !next || next === currentName) {
      cancelEditName();
      return;
    }
    try {
      await updateNameMutation.mutateAsync({ templateId, name: next });
      toast.success('Template renamed');
      setIsEditingName(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to rename template');
    }
  };

  // Show loading while checking access or loading template
  if (!user || templateQuery.isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      </Page>
    );
  }

  if (!hasAccess) return null;

  if (templateQuery.isError || !template) {
    return (
      <Page>
        <PageHeader
          title="Template Not Found"
          description="The requested environment template could not be found"
        />
        <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
          <Text className="text-muted-foreground">
            This template may have been deleted or you may not have access.
          </Text>
          <Button
            variant="outline"
            size="sm"
            onPress={() => navigate({ to: '/sandboxes' })}
            className="mt-4"
          >
            Back to Test Environments
          </Button>
        </div>
      </Page>
    );
  }

  const currentInstanceSize: InstanceSize = template.instanceSize;

  const handleInstanceSizeChange = (next: InstanceSize) => {
    if (next === currentInstanceSize) return;
    updateInstanceSizeMutation.mutate(
      { templateId: template.id, instanceSize: next },
      {
        onSuccess: () => {
          toast.success(
            `Instance size set to ${next} (${INSTANCE_SIZE_SPEC[next].ec2InstanceType}). Next provision will use the new size.`
          );
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Failed to update instance size');
        },
      }
    );
  };

  const headerDescription = (
    <Flex align="center" gap="sm" wrap>
      {template.repositoryFullName ? (
        <Flex align="center" gap="xs">
          <GitBranch size={14} className="text-muted-foreground" />
          <Text size="sm" className="text-muted-foreground">
            {template.repositoryFullName} @ {template.branch}
          </Text>
        </Flex>
      ) : (
        <Text size="sm" className="text-muted-foreground">
          No repository (empty workspace)
        </Text>
      )}
      <Text size="sm" className="text-muted-foreground">
        · {currentInstanceSize}
      </Text>
      <Text size="sm" className="text-muted-foreground">
        · Created {formatRelative(template.createdAt)}
      </Text>
      {template.lastVerifiedAt && (
        <Text size="sm" className="text-muted-foreground">
          / Verified {formatRelative(template.lastVerifiedAt)}
        </Text>
      )}
    </Flex>
  );

  const titleNode = isEditingName ? (
    <Flex align="center" gap="xs">
      <TextField
        aria-label="Template name"
        value={nameDraft}
        onChange={setNameDraft}
        autoFocus
        isDisabled={updateNameMutation.isPending}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void submitName(template.name);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelEditName();
          }
        }}
        className="w-72 text-base"
      />
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        aria-label="Save name"
        isDisabled={updateNameMutation.isPending}
        onPress={() => void submitName(template.name)}
      >
        <Check size={18} />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        aria-label="Cancel rename"
        isDisabled={updateNameMutation.isPending}
        onPress={cancelEditName}
      >
        <X size={18} />
      </Button>
    </Flex>
  ) : (
    <span className="group inline-flex items-center gap-2">
      {template.name}
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        aria-label="Rename template"
        className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
        onPress={() => startEditName(template.name)}
      >
        <PencilSimple size={16} className="text-muted-foreground" />
      </Button>
    </span>
  );

  return (
    <Page>
      <PageHeader
        icon={<Cube size={24} weight="fill" />}
        eyebrow={<SandboxStatusBadge status={template.status} />}
        title={titleNode}
        description={headerDescription}
      />

      <TemplateStatusPanels
        template={template}
        isStartingAuthoring={startAuthoringMutation.isPending}
        isRegenerating={regenerateMutation.isPending}
        onStartAuthoring={handleStartAuthoring}
        onRegenerate={handleRegenerate}
      />

      {/* Tab Navigation */}
      <UnderlineTabs
        selectedId={activeTab}
        onSelectionChange={(id) => setActiveTab(id as TabId)}
        className="mb-6"
        layoutId="template-detail-tabs"
      >
        <UnderlineTab id="instances">
          Instances{instanceStats.total > 0 && ` (${instanceStats.total})`}
        </UnderlineTab>
        <UnderlineTab id="configuration">Configuration</UnderlineTab>
        <UnderlineTab id="actions">Actions</UnderlineTab>
        <UnderlineTab id="template">Template</UnderlineTab>
      </UnderlineTabs>

      {/* Tab Content */}
      {activeTab === 'instances' && (
        <TemplateInstancesSection
          template={template}
          templateId={templateId}
          onStatsChange={setInstanceStats}
        />
      )}

      {activeTab === 'configuration' && (
        <ConfigurationTab
          template={template}
          configDefaults={configDefaults}
          configuredSensitiveKeys={template.configuredSensitiveKeys ?? []}
          unsetConfigKeys={[...unsetConfigKeys]}
          visibleFields={visibleFields}
          isDirty={dirtyConfigKeys.size > 0 || unsetConfigKeys.size > 0}
          isSaving={updateConfigMutation.isPending}
          onUpdateConfig={updateConfigDefault}
          onClearConfig={clearConfigDefault}
          onToggleVisibility={toggleFieldVisibility}
          onSave={handleSaveConfig}
          emulationConfig={emulationConfig}
          emulationDirty={emulationDirty}
          isEmulationSaving={updateEmulationMutation.isPending}
          onUpdateEmulation={handleUpdateEmulation}
          onSaveEmulation={handleSaveEmulation}
          instanceSize={currentInstanceSize}
          onInstanceSizeChange={handleInstanceSizeChange}
          isInstanceSizeSaving={updateInstanceSizeMutation.isPending}
        />
      )}

      {activeTab === 'actions' && <TemplateActionsTab templateId={templateId} />}

      {activeTab === 'template' && (
        <TemplateTab
          template={template}
          templateId={templateId}
          onDelete={() => setDeleteDialogOpen(true)}
          isDeleting={deleteMutation.isPending}
        />
      )}

      <TemplateDeleteDialog
        isOpen={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        templateName={template.name}
        activeInstanceCount={instanceStats.active}
        isDeleting={deleteMutation.isPending}
        onDelete={handleDelete}
      />
    </Page>
  );
};
